import React from 'react';
import { Home, Calendar, HelpCircle, ArrowLeft, LayoutDashboard } from 'lucide-react';

interface MentorshipSidebarProps {
  activeTab: 'inicio' | 'eventos' | 'suporte' | 'dashboard';
  onTabChange: (tab: 'inicio' | 'eventos' | 'suporte' | 'dashboard') => void;
  onBackToMainPlatform?: () => void;
  accentColor?: string;
  isMentorOrAdmin?: boolean;
}

export const MentorshipSidebar: React.FC<MentorshipSidebarProps> = ({
  activeTab,
  onTabChange,
  onBackToMainPlatform,
  accentColor = '#0FFF55',
  isMentorOrAdmin = false,
}) => {
  const navItems = [
    { id: 'inicio' as const, label: 'Início', icon: Home },
    ...(isMentorOrAdmin ? [{ id: 'dashboard' as const, label: 'Dashboard', icon: LayoutDashboard }] : []),
    { id: 'eventos' as const, label: 'Eventos', icon: Calendar },
    { id: 'suporte' as const, label: 'Suporte', icon: HelpCircle },
  ];

  return (
    <aside className="fixed left-0 top-0 bottom-0 z-40 hidden md:flex flex-col items-center justify-between w-[70px] xl:w-[76px] bg-[#0d0e11] border-r border-white/5 py-5 select-none transition-all duration-300">
      {/* Top Logo / Hubla-style Icon */}
      <div className="flex flex-col items-center gap-6">
        <button
          onClick={onBackToMainPlatform}
          title="Voltar à plataforma principal"
          className="w-10 h-10 rounded-xl bg-[#17181c] hover:bg-white/10 text-white flex items-center justify-center border border-white/10 transition-all group"
        >
          <ArrowLeft className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
        </button>

        {/* Brand Dot Accent */}
        <div
          className="w-2.5 h-2.5 rounded-full animate-pulse"
          style={{ backgroundColor: accentColor, boxShadow: `0 0 12px ${accentColor}` }}
        />
      </div>

      {/* Nav Items */}
      <nav className="flex flex-col items-center gap-6 w-full px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;

          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`relative flex flex-col items-center justify-center w-full py-3 px-1 rounded-xl transition-all group ${
                isActive
                  ? 'text-white bg-white/10'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {/* Active Left Indicator Bar */}
              {isActive && (
                <div
                  className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full"
                  style={{ backgroundColor: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
                />
              )}

              <Icon
                className={`w-5 h-5 mb-1 transition-transform group-hover:scale-110 ${
                  isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'
                }`}
                style={{ color: isActive ? accentColor : undefined }}
              />
              <span className="text-[10px] font-medium tracking-tight">
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>

      {/* Bottom Profile / Masterclub Badge */}
      <div className="flex flex-col items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#0FFF55] to-[#4facfe] p-[1px]">
          <div className="w-full h-full bg-[#121316] rounded-full flex items-center justify-center text-[10px] font-bold text-white">
            MC
          </div>
        </div>
      </div>
    </aside>
  );
};
